import React, { useState } from 'react';
import { signUp, signIn, resetPassword } from '../services/supabase';
import { TIERS } from '../config/tiers';
import { getUsageSummary } from '../utils/rateLimit';
import { Link } from 'react-router-dom';
import { exportInventory, exportRecipes, exportShoppingList, exportBudgetExpenses, exportWorkoutLog } from '../utils/csvExport';

const Settings = ({
  user, syncStatus, signOut, tier,
  inventory, recipes, shoppingList, budget, workoutLog,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState('signin');
  const [authStatus, setAuthStatus] = useState('');

  const tierConfig = TIERS[tier] || TIERS.commis;
  const usage = getUsageSummary(tier);

  const handleAuth = async () => {
    if (!email.trim() || !password.trim()) {
      setAuthStatus('Please enter email and password.');
      return;
    }
    if (password.length < 6) {
      setAuthStatus('Password must be at least 6 characters.');
      return;
    }

    try {
      setAuthStatus(authMode === 'signup' ? 'Creating account...' : 'Signing in...');
      if (authMode === 'signup') {
        const data = await signUp(email.trim(), password);
        if (data?.user && !data.session) {
          setAuthStatus('Account created! Check your email to confirm, then sign in.');
        } else if (data?.user?.identities?.length === 0) {
          setAuthStatus('An account with this email already exists. Try signing in instead.');
        } else {
          setAuthStatus('Account created! Check your email to confirm, then sign in.');
        }
      } else {
        await signIn(email.trim(), password);
        setAuthStatus('');
      }
    } catch (err) {
      const msg = err.message || 'Authentication failed';
      if (msg.includes('User already registered')) {
        setAuthStatus('An account with this email already exists. Try signing in instead.');
      } else if (msg.includes('Invalid login credentials')) {
        setAuthStatus('Incorrect email or password.');
      } else {
        setAuthStatus(msg);
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setAuthStatus('Enter your email above, then click Forgot Password.');
      return;
    }
    try {
      setAuthStatus('Sending reset link...');
      await resetPassword(email.trim());
      setAuthStatus('Password reset email sent! Check your inbox.');
    } catch (err) {
      setAuthStatus(err.message || 'Failed to send reset email.');
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Settings</h1>

      {/* Current Plan */}
      <div className="settings-section">
        <h2 className="settings-section-title">Current Plan</h2>
        <div className="budget-overview" style={{ marginBottom: '12px' }}>
          <div className="fitness-stats-grid">
            <div className="fitness-stat-card">
              <span className="fitness-stat-label">Plan</span>
              <span className="fitness-stat-value" style={{ fontSize: '16px' }}>{tierConfig.name}</span>
              <span className="fitness-stat-sub">{tierConfig.tagline}</span>
            </div>
            <div className="fitness-stat-card">
              <span className="fitness-stat-label">Recipe AI</span>
              <span className="fitness-stat-value">{usage.recipeAI.used}/{usage.recipeAI.limit}</span>
              <span className="fitness-stat-sub">this month</span>
            </div>
            <div className="fitness-stat-card">
              <span className="fitness-stat-label">Overload AI</span>
              <span className="fitness-stat-value">{usage.overloadAI.used}/{usage.overloadAI.limit}</span>
              <span className="fitness-stat-sub">this month</span>
            </div>
          </div>
        </div>
        <Link to="/upgrade" className="btn btn-primary btn-block" style={{ textDecoration: 'none', textAlign: 'center' }}>
          {tier === 'chef' ? 'Manage Plan' : 'View Plans & Upgrade'}
        </Link>
      </div>

      {/* Account & Cloud Sync */}
      <div className="settings-section">
        <h2 className="settings-section-title">Account & Cloud Sync</h2>
        <p className="settings-description">
          Sign in to sync your data automatically across devices. Changes save to the cloud within a few seconds.
        </p>

        {user ? (
          <div>
            <div className="settings-connected">
              <span className="settings-connected-dot" aria-hidden="true"></span>
              <p className="settings-connected-text">
                Signed in as <strong>{user.email}</strong>
              </p>
            </div>
            <p className="auth-sync-hint">
              Auto-sync is active. Your data saves automatically when you make changes.
            </p>
            <button onClick={signOut} className="btn btn-danger">
              Sign Out
            </button>
          </div>
        ) : (
          <div>
            <div className="tag-list" style={{ marginBottom: '16px' }}>
              <button className={`tag${authMode === 'signin' ? ' active' : ''}`} onClick={() => { setAuthMode('signin'); setAuthStatus(''); }}>
                Sign In
              </button>
              <button className={`tag${authMode === 'signup' ? ' active' : ''}`} onClick={() => { setAuthMode('signup'); setAuthStatus(''); }}>
                Create Account
              </button>
            </div>

            <div className="field-floating">
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=" "
                className="field-floating-input"
                autoComplete="email"
              />
              <label htmlFor="auth-email">Email</label>
            </div>

            <div className="field-floating">
              <input
                id="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=" "
                className="field-floating-input"
                autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
              />
              <label htmlFor="auth-password">Password</label>
            </div>

            <button onClick={handleAuth} className="btn btn-primary btn-block">
              {authMode === 'signup' ? 'Create Account' : 'Sign In'}
            </button>

            {authMode === 'signin' && (
              <button
                onClick={handleForgotPassword}
                className="btn btn-ghost btn-block"
                style={{ marginTop: '8px', fontSize: '13px' }}
              >
                Forgot Password?
              </button>
            )}

            {authStatus && <p className="auth-status">{authStatus}</p>}
          </div>
        )}
      </div>

      {/* CSV Export */}
      <div className="settings-section">
        <h2 className="settings-section-title">Data Export (CSV)</h2>
        <p className="settings-description">
          Export your data as CSV files for use in spreadsheets, analytics tools, or backup.
        </p>
        <div className="btn-group" style={{ flexWrap: 'wrap' }}>
          <button onClick={() => exportInventory(inventory || [])} className="btn btn-secondary btn-sm" disabled={!inventory || inventory.length === 0}>
            Export Inventory
          </button>
          <button onClick={() => exportRecipes(recipes || [])} className="btn btn-secondary btn-sm" disabled={!recipes || recipes.length === 0}>
            Export Recipes
          </button>
          <button onClick={() => exportShoppingList(shoppingList || [])} className="btn btn-secondary btn-sm" disabled={!shoppingList || shoppingList.length === 0}>
            Export Shopping List
          </button>
          <button onClick={() => exportBudgetExpenses(budget?.expenses || [])} className="btn btn-secondary btn-sm" disabled={!budget?.expenses || budget.expenses.length === 0}>
            Export Budget
          </button>
          <button onClick={() => exportWorkoutLog(workoutLog || [])} className="btn btn-secondary btn-sm" disabled={!workoutLog || workoutLog.length === 0}>
            Export Workout Log
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
